import { BrowserJsPlumbInstance } from '@jsplumb/community';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook to cleanup virtual ports when the component unmounts.
 * IMPORTANT: This hook exists because when users navigate away from a custom widget the dom element that anchors the points
 * ceases to exist, thus making it impossible to remove the virtual ports attached to it. Here we keep track of dom elements
 * so that the virtual ports can be removed when the component unmounts or when the cleanup function is called.
 */
const useVirtualPortCleanup = (plumbInstance: BrowserJsPlumbInstance | null) => {
  const virtualPortElementsRef = useRef<Record<string, {
    elementId: string,
    widgetId: string,
    portName: string,
  }>>({});
  
  const registerVirtualPort = useCallback((config: {
    /** the id of the DOM element that represents the port */
    elementId: string,
    widgetId: string,
    portName: string,
    }) => {
    const { elementId, widgetId, portName } = config;
      virtualPortElementsRef.current[elementId] = { elementId, widgetId, portName };
  }, []);
  
  const unregisterVirtualPort = useCallback((elementId: string) => {
      delete virtualPortElementsRef.current[elementId];
  }, []);
  
  const unregisterVirtualPortByWidgetId = useCallback((widgetId: string) => {
    const elementsToRemove = Object.keys(virtualPortElementsRef.current).filter((elementId) => virtualPortElementsRef.current[elementId].widgetId === widgetId);
    if(elementsToRemove.length && plumbInstance) {
      elementsToRemove.forEach((elementId) => {
        plumbInstance.deleteConnectionsForElement(elementId, { fireEvent: false });
        plumbInstance.removeAllEndpoints(elementId);
        plumbInstance.unmanage(elementId);
      });
    }
  }, [plumbInstance]);

  const cleanupAllVirtualPorts = useCallback(() => {
    const elementsToRemove = Object.keys(virtualPortElementsRef.current);
    if(elementsToRemove.length && plumbInstance) {
        elementsToRemove.forEach((elementId) => {
            plumbInstance.deleteConnectionsForElement(elementId, { fireEvent: false });
            plumbInstance.removeAllEndpoints(elementId);
            plumbInstance.unmanage(elementId);
        });

        virtualPortElementsRef.current = {};
    }
  }, [plumbInstance]);
  
  // Cleanup before component unmounts
  useEffect(() => {
      return () => {
          cleanupAllVirtualPorts();
      };
  }, [cleanupAllVirtualPorts]);
  
  return { registerVirtualPort, unregisterVirtualPort, cleanupAllVirtualPorts, unregisterVirtualPortByWidgetId };
};

export default useVirtualPortCleanup;